import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type TransferCarrierFormSchema, transferCarrierFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { TransferCarrierForm } from "@/forms/transfer-carrier-form";
import { client } from "@/hono";

/** Props Interface */
interface AddTransferCarrierModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
}

/**
 * Create a transfer carrier — a trolley, a roll cage, a forklift pallet.
 *
 * It opens with the number empty, which is the ordinary case: the database allocates the
 * organisation's next one and somebody prints that label. A device that already carries a painted
 * number is entered with it instead, and the trigger keeps whatever is passed.
 */
export function AddTransferCarrierModal({
	onClose,
	isOpen,
	onComplete,
}: AddTransferCarrierModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(transferCarrierFormSchema),
		defaultValues: {
			name: null,
			description: null,
			number: null,
			currentZoneId: null,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: TransferCarrierFormSchema) {
		const response = await client.stock["transfer-carriers"].$post({ json });
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.add_transfer_carrier")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<TransferCarrierForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
